import Board from "components/Common/Board/Board";
import Button from "components/foundation/Button";
import Divider from "components/foundation/Divider";
import Input from "components/foundation/Input/Input";
import Modal from "components/foundation/Modal/Modal";
import React, { useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";
import InfoListItem from "../InfoListItem";
import SignImage from "../SignImage";
import useSignImage from "features/user/hooks/common/useSignImage";
import Select from "components/foundation/Select/Select";
import Toggle from "components/foundation/Toggle/Toggle";
import { UserDto } from "apis/data-contracts";
import useUserAuthoritiesQuery from "features/user/hooks/queries/useUserAuthoritiesQuery";
import dateFormat from "utils/dateFormat";
import useUserUpdateByAdminMutation from "features/user/hooks/mutations/useUserUpdateByAdminMutation";
import usePresignedUrlMutation from "features/upload/hooks/mutations/usePresignedUrlMutation";
import Toast from "components/foundation/Toast";
import { ROUTER_PATH } from "config/path";
import useUserDeleteByAdminMutation from "features/user/hooks/mutations/useUserDeleteByAdminMutation";
import { useNavigate } from "react-router-dom";
import formatPhoneNumber from "utils/formatUtils/formatPhone";
import formatMinMaxLengthMessage from "utils/formatUtils/formatMinMaxLengthMessage";
import { UserProfileDescription } from "components/myInfo/UserProfileFormCard/styles";

interface FormData {
  companyName: string;
  companyDepartment: string;
  companyPosition: string;
  authority: string;
  isAdmin: boolean;
  isDecisionManager: boolean;
  usedDayOffCount: number;
  maxDayOffCount: number;
}

interface UserDetailMobilePageProps {
  userData: UserDto;
  disableUnloadTracking: () => void;
}

const UserDetailMobilePage = ({
  userData,
  disableUnloadTracking,
}: UserDetailMobilePageProps) => {
  const { register, control, getValues, reset, watch, setValue } =
    useFormContext<FormData>();

  const companyName = watch("companyName");
  const companyDepartment = watch("companyDepartment");
  const companyPosition = watch("companyPosition");
  const maxDayOffCount = watch("maxDayOffCount");

  const isSaveDisabled =
    companyName?.length >= 20 ||
    companyName?.length < 2 ||
    companyDepartment?.length >= 20 ||
    companyPosition?.length >= 20 ||
    maxDayOffCount < 0 ||
    maxDayOffCount?.toString().length > 10;

  const {
    imageSrc,
    fileInputProps,
    inputFor,
    isError,
    handleSignDelete,
    signFile,
  } = useSignImage(userData.signImageUrl);
  const navigate = useNavigate();

  const { data: authoritiesData } = useUserAuthoritiesQuery();
  const { mutate: deleteUser } = useUserDeleteByAdminMutation();
  const { mutate: saveUser } = useUserUpdateByAdminMutation();
  const { mutate: imageUpload } = usePresignedUrlMutation({
    fileType: "SIGNATURE",
    isOverwrite: true,
    allowedExtensions: [".png", ".jpeg"],
  });

  const [isSuccessToast, setIsSuccessToast] = useState(false);
  const [isDeleteToast, setIsDeleteToast] = useState(false);
  const [isDayOffErrorModal, setIsDayOffErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const maxDayOffCountRef = useRef<HTMLInputElement>(null);

  const handleDelete = () => {
    deleteUser(
      {
        userUuid: userData.uuid,
      },
      {
        onSuccess: () => {
          setIsDeleteToast(true);
          setTimeout(() => {
            navigate(ROUTER_PATH.user.dashboard._path);
          }, 2000);
        },
      },
    );
  };

  const handleSave = async () => {
    let url = undefined;

    if (signFile) {
      url = await imageUpload(signFile, {
        fileName: `${userData.uuid}-signed-image-${signFile.name}`,
      });
    }
    saveUser(
      {
        userUuid: userData.uuid,
        companyName: getValues("companyName"),
        companyDepartment: getValues("companyDepartment"),
        companyPosition: getValues("companyPosition"),
        authorityUuid: getValues("authority"),
        maxDayOffCount: getValues("maxDayOffCount"),
        isAdmin: getValues("isAdmin"),
        signImageUrl: url,
        isDecisionManager: getValues("isDecisionManager"),
      },
      {
        onSuccess: () => {
          setIsSuccessToast(true);
          disableUnloadTracking();
          reset(getValues());
          setTimeout(() => {
            navigate(ROUTER_PATH.user.dashboard._path);
          }, 2000);
        },

        onError: (error: any) => {
          if (error.response?.data?.code === 400) {
            setErrorMessage(error.response?.data?.message);
            setIsDayOffErrorModal(true);
          }
        },
      },
    );
  };

  const isApprove = userData.approvedAt !== null;

  return (
    <>
      {/* 1회성 에러, error 상태는 유지해야 함 */}
      {isError.size && (
        <Modal.Root defaultOpen={true}>
          <Modal.Body title="200KB 이하 파일만 등록 가능합니다." onConfirm>
            파일 용량을 확인해 주세요.
          </Modal.Body>
        </Modal.Root>
      )}
      <UserDetailPageLayout>
        <h1>{userData.name}</h1>
        <span>{userData.name}님의 회원가입 정보입니다.</span>
        <div className="detail-content">
          <InfoListItem title="회사" direction="column">
            <Input
              {...register("companyName")}
              errorMessage={
                companyName?.length >= 20 || companyName?.length < 2
                  ? formatMinMaxLengthMessage(2, 20)
                  : undefined
              }
              maxLength={20}
              disabled={!isApprove}
            />
          </InfoListItem>
          <InfoListItem title="부서" direction="column">
            <Input
              {...register("companyDepartment")}
              errorMessage={
                companyDepartment?.length >= 20
                  ? formatMinMaxLengthMessage(0, 20)
                  : undefined
              }
              maxLength={20}
              disabled={!isApprove}
            />
          </InfoListItem>
          <InfoListItem title="직급" direction="column">
            <Input
              {...register("companyPosition")}
              errorMessage={
                companyPosition?.length >= 20
                  ? formatMinMaxLengthMessage(0, 20)
                  : undefined
              }
              maxLength={20}
              disabled={!isApprove}
            />
          </InfoListItem>
          <InfoListItem title="권한" direction="column">
            <Controller
              control={control}
              name="authority"
              render={({ field: { onChange, value } }) => (
                <Select
                  value={value}
                  style={{ width: "100%" }}
                  list={authoritiesData.map((authority) => ({
                    value: authority?.uuid ?? "",
                    label: authority?.name ?? "",
                  }))}
                  onValueChange={onChange}
                  disabled={!isApprove}
                />
              )}
            />
          </InfoListItem>
          <InfoListItem title="연간 휴가" direction="column">
            <Input
              {...register("maxDayOffCount")}
              ref={maxDayOffCountRef}
              type="number"
              defaultValue={userData.maxDayOffCount}
              errorMessage={
                maxDayOffCount < 0
                  ? "0 이상 입력 가능"
                  : maxDayOffCount?.toString().length > 10
                    ? formatMinMaxLengthMessage(0, 10)
                    : undefined
              }
              disabled={!isApprove}
              onWheel={(event) => event.currentTarget.blur()}
              onChange={(event) => {
                const { value } = event.target;

                if (/^\d+(\.\d{1})?$/.test(value)) {
                  setValue("maxDayOffCount", Number(value));
                } else {
                  const fixedValue = Number(value).toFixed(1);
                  setValue("maxDayOffCount", Number(fixedValue));
                }
              }}
            />
            <UserProfileDescription>
              사용된 휴가 수보다 작은 숫자는 입력 불가능
            </UserProfileDescription>
            <UserProfileDescription>
              금년 기준 사용 휴가 일수 : {userData.usedDayOffCount}일
            </UserProfileDescription>
          </InfoListItem>

          {isDayOffErrorModal && (
            <Modal.Root
              open={isDayOffErrorModal}
              onOpenChange={setIsDayOffErrorModal}
            >
              <Modal.Body
                title={errorMessage}
                onConfirm={() => {
                  setIsDayOffErrorModal(false);
                  setTimeout(() => {
                    maxDayOffCountRef.current?.focus();
                  }, 0);
                }}
              />
            </Modal.Root>
          )}

          <InfoListItem title="이메일">
            <span>{userData.email}</span>
          </InfoListItem>
          <InfoListItem title="연락처">
            <span>{formatPhoneNumber(userData.phoneNumber)}</span>
          </InfoListItem>
          <InfoListItem title="서명 이미지">
            <input
              style={{
                display: "none",
              }}
              type="file"
              {...fileInputProps}
              disabled={!isApprove}
            />
            <SignImage imgWrapperProps={inputFor} src={imageSrc ?? undefined} />
            <div
              style={{
                marginTop: "1.6rem",
                display: "flex",
                gap: "1rem",
              }}
            >
              <Button
                variant="tertiary-neg"
                size="small"
                onClick={handleSignDelete}
                disabled={!isApprove}
              >
                삭제
              </Button>

              <Button
                variant="tertiary"
                size="small"
                {...inputFor}
                disabled={!isApprove}
              >
                파일 첨부
              </Button>
            </div>
          </InfoListItem>
          <InfoListItem title="가입일">
            <span>{dateFormat(userData.createdAt, "YYYY-MM-DD hh:mm")}</span>
          </InfoListItem>
          <InfoListItem title="승인일">
            <span>{dateFormat(userData.approvedAt, "YYYY-MM-DD hh:mm")}</span>
          </InfoListItem>
          <InfoListItem title="수정일">
            <span>{dateFormat(userData.updatedAt, "YYYY-MM-DD hh:mm")}</span>
          </InfoListItem>
          <InfoListItem title="관리자 지정">
            <Controller
              control={control}
              name="isAdmin"
              render={({ field: { onChange, value } }) => (
                <Toggle pressed={value} onPressedChange={onChange} />
              )}
            />
          </InfoListItem>
          <InfoListItem title="전결권자 지정">
            <Controller
              control={control}
              name="isDecisionManager"
              render={({ field: { onChange, value } }) => (
                <Toggle pressed={value} onPressedChange={onChange} />
              )}
            />
          </InfoListItem>
        </div>
        <Divider />
        <div className="detail-footer">
          <Toast.Message open={isSuccessToast} onOpenChange={setIsSuccessToast}>
            변경 내용이 저장되었습니다.
          </Toast.Message>
          <Toast.Message open={isDeleteToast} onOpenChange={setIsDeleteToast}>
            해당 유저가 탈퇴 처리되었습니다.
          </Toast.Message>
          <Modal.Root>
            <Modal.Body
              title="강제 탈퇴를 진행하시겠습니까?"
              onConfirm={handleDelete}
              onCancel
            >
              {"'확인' 버튼 클릭 시 해당 계정은 삭제됩니다."}
            </Modal.Body>
            <Modal.Trigger asChild>
              <Button variant="tertiary-neg" size="large">
                강제 탈퇴
              </Button>
            </Modal.Trigger>
          </Modal.Root>
          <Modal.Root>
            <Modal.Body
              title="변경 내용을 저장하시겠습니까?"
              onConfirm={handleSave}
              onCancel
            ></Modal.Body>
            <Modal.Trigger asChild>
              <Button variant="primary" size="large" disabled={isSaveDisabled}>
                저장
              </Button>
            </Modal.Trigger>
          </Modal.Root>
        </div>
      </UserDetailPageLayout>
    </>
  );
};

export default UserDetailMobilePage;

const UserDetailPageLayout = styled(Board)`
  padding: 1.6rem 1.6rem 1.6rem 1.6rem;
  margin: auto;
  ${({ theme }) => theme.typography.body3.regular};

  h1 {
    ${({ theme }) => theme.typography.t1.semibold};
    margin-bottom: 0.8rem;
  }

  > span {
    color: ${({ theme }) => theme.color.text.secondary};
  }

  .detail-content {
    display: flex;
    gap: 2.8rem;
    flex-direction: column;
    margin: 3rem 0rem;
  }
  .detail-footer {
    margin: 2rem 1.6rem 0rem;

    width: -webkit-fill-available;

    display: flex;
    gap: 1rem;
    > button {
      flex: 1;
    }
  }
`;
